/**
 * VFS filename collision resolver.
 *
 * When uploading `test.md` into a folder that already contains `test.md`,
 * we want the new file to land as `test (1).md`, then `test (2).md`, and
 * so on — matching the macOS/Finder + Chrome download convention.
 *
 * Shared between the VFS file tree (`AuiFiles`) and the chat composer
 * attachments (`ComposerAttachmentsProvider`) so both surfaces resolve
 * collisions the same way.
 */

/**
 * Pick a basename that doesn't collide with anything in `reservedNames`.
 * Returns `desiredName` unchanged when it's already free.
 *
 * Naming progression: `test.md` → `test (1).md` → `test (2).md` → …
 * Dotfiles (e.g. `.env`) keep the whole name as the base — the rename
 * becomes `.env (1)` (no extension to preserve).
 */
export function resolveCollisionFreeName(
  desiredName: string,
  reservedNames: ReadonlySet<string>,
): string {
  if (!reservedNames.has(desiredName)) return desiredName;
  const { base, ext } = splitBaseExt(desiredName);
  for (let n = 1; n < 1000; n++) {
    const candidate = `${base} (${n})${ext}`;
    if (!reservedNames.has(candidate)) return candidate;
  }
  // Pathological case (1000 same-named files in one folder). Fall back
  // to a short timestamp suffix instead of looping forever.
  return `${base} (${Date.now().toString(36)})${ext}`;
}

/**
 * Same as `resolveCollisionFreeName` but works against a set of full
 * paths. Returns both the chosen basename and the joined path, which is
 * what callers usually want anyway.
 */
export function resolveCollisionFreePath(
  desiredName: string,
  folderPath: string,
  reservedPaths: ReadonlySet<string>,
): { name: string; path: string } {
  const folder = folderPath.replace(/^\/+|\/+$/g, '');
  const reservedNames = new Set<string>();
  const prefix = folder ? `${folder}/` : '';
  for (const p of reservedPaths) {
    // Only siblings inside the target folder can collide. We don't
    // restrict to immediate siblings (no slash check after prefix)
    // because conflicts only arise on exact-path matches anyway, and a
    // looser membership keeps the helper cheap to feed.
    if (p === folder) continue;
    if (prefix && !p.startsWith(prefix)) continue;
    const rest = prefix ? p.slice(prefix.length) : p;
    if (rest.includes('/')) continue;
    reservedNames.add(rest);
  }
  const name = resolveCollisionFreeName(desiredName, reservedNames);
  const path = folder ? `${folder}/${name}` : name;
  return { name, path };
}

/**
 * Split `report.final.pdf` → `{ base: "report.final", ext: ".pdf" }`.
 * Dotfiles like `.env` and extension-less names return `ext: ""`.
 */
function splitBaseExt(name: string): { base: string; ext: string } {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return { base: name, ext: '' };
  return { base: name.slice(0, dot), ext: name.slice(dot) };
}
